<script setup>
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { X } from '@lucide/vue'

import { listNovelPacks } from '../../api/novel/packs.js'
import { useNovelPageText } from './novel-page-text.js'

// 输入即搜：候选取作品列表接口 q（服务端按标题、slug、创作者邮箱模糊匹配）的前 20 条。
const CANDIDATE_LIMIT = 20
const SEARCH_DELAY_MS = 250

const props = defineProps({
  // 已选作品 { id, title, slug }；null 表示全部作品。
  modelValue: { type: Object, default: null },
  label: { type: String, required: true },
  placeholder: { type: String, required: true },
  emptyText: { type: String, required: true },
  loadingText: { type: String, required: true },
  clearLabel: { type: String, required: true },
})
const emit = defineEmits(['update:modelValue'])

const { apiError } = useNovelPageText()
const listId = useId()
const inputEl = ref(null)
const open = ref(false)
const query = ref('')
const candidates = ref([])
const loading = ref(false)
const error = ref(null)
const activeIndex = ref(-1)
const panelStyle = ref({})
let searchTimer = null
let searchSeq = 0

const selectedLabel = computed(() => (props.modelValue ? `${props.modelValue.title} · ${props.modelValue.slug}` : ''))

// 不在输入时，输入框显示已选作品；外部改了选择（点对局行里的作品名、重置）也跟着变。
watch(selectedLabel, (value) => {
  if (!open.value) query.value = value
}, { immediate: true })

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
  window.removeEventListener('scroll', placePanel, true)
  window.removeEventListener('resize', placePanel)
})

const optionId = (index) => `${listId}-${index}`

function openPanel() {
  if (open.value) return
  open.value = true
  placePanel()
  window.addEventListener('scroll', placePanel, true)
  window.addEventListener('resize', placePanel)
  search(query.value === selectedLabel.value ? '' : query.value)
}

function closePanel() {
  if (!open.value) return
  open.value = false
  clearTimeout(searchTimer)
  window.removeEventListener('scroll', placePanel, true)
  window.removeEventListener('resize', placePanel)
  query.value = selectedLabel.value
}

// 候选面板挂到 body 上用 fixed 定位：工具栏所在的 Section 会裁掉溢出的内容。
function placePanel() {
  const rect = inputEl.value?.getBoundingClientRect()
  if (!rect) return
  panelStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.left}px`, width: `${rect.width}px` }
}

function onInput() {
  openPanel()
  // 等待期间标成加载中，回车不会选到上一轮的旧候选。
  loading.value = true
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => search(query.value), SEARCH_DELAY_MS)
}

/** 只认最后一次搜索：输入快时晚回来的旧请求不覆盖新结果。 */
async function search(text) {
  const seq = ++searchSeq
  loading.value = true
  error.value = null
  try {
    const result = await listNovelPacks({ q: text.trim() || undefined, page: 1, page_size: CANDIDATE_LIMIT })
    if (seq !== searchSeq) return
    candidates.value = result.items
    activeIndex.value = result.items.length > 0 ? 0 : -1
  } catch (requestError) {
    if (seq !== searchSeq) return
    candidates.value = []
    activeIndex.value = -1
    error.value = apiError(requestError)
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

/** @param {{ id: string, title: string, slug: string }|null} pack */
function choose(pack) {
  emit('update:modelValue', pack === null ? null : { id: pack.id, title: pack.title, slug: pack.slug })
  closePanel()
}

function moveActive(step) {
  const count = candidates.value.length
  if (count === 0) return
  activeIndex.value = (activeIndex.value + step + count) % count
  nextTick(() => document.getElementById(optionId(activeIndex.value))?.scrollIntoView({ block: 'nearest' }))
}

function onKeydown(event) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    if (open.value) moveActive(event.key === 'ArrowDown' ? 1 : -1)
    else openPanel()
  } else if (event.key === 'Enter' && open.value && !loading.value && activeIndex.value >= 0) {
    event.preventDefault()
    choose(candidates.value[activeIndex.value])
  } else if (event.key === 'Escape' && open.value) {
    event.preventDefault()
    closePanel()
  }
}
</script>

<template>
  <div class="npp">
    <input
      ref="inputEl"
      v-model="query"
      class="npp__input"
      type="text"
      role="combobox"
      autocomplete="off"
      aria-autocomplete="list"
      :aria-label="label"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="listId"
      :aria-activedescendant="open && activeIndex >= 0 ? optionId(activeIndex) : undefined"
      :placeholder="placeholder"
      data-testid="novel-pack-picker-input"
      @focus="openPanel"
      @click="openPanel"
      @input="onInput"
      @keydown="onKeydown"
      @blur="closePanel"
    />
    <button v-if="modelValue" type="button" class="npp__clear" :aria-label="clearLabel" :title="clearLabel" data-testid="novel-pack-picker-clear" @click="choose(null)">
      <X :size="14" aria-hidden="true" />
    </button>
    <Teleport to="body">
      <ul v-if="open" :id="listId" class="npp__panel" role="listbox" :aria-label="label" :aria-busy="loading ? 'true' : 'false'" :style="panelStyle" @mousedown.prevent>
        <li v-if="error" class="npp__note npp__note--error" role="presentation" :title="error.title">{{ error.text }}</li>
        <template v-else-if="candidates.length > 0">
          <li
            v-for="(pack, index) in candidates"
            :id="optionId(index)"
            :key="pack.id"
            class="npp__option"
            :class="{ 'is-active': index === activeIndex }"
            role="option"
            :aria-selected="index === activeIndex ? 'true' : 'false'"
            :data-testid="`novel-pack-picker-option-${pack.id}`"
            @mousemove="activeIndex = index"
            @click="choose(pack)"
          >
            <span class="npp__title">{{ pack.title }}</span>
            <span class="npp__meta">{{ pack.slug }}<template v-if="pack.owner_email"> · {{ pack.owner_email }}</template></span>
          </li>
        </template>
        <li v-else class="npp__note" role="presentation">{{ loading ? loadingText : emptyText }}</li>
      </ul>
    </Teleport>
  </div>
</template>

<style scoped>
.npp { position: relative; display: flex; align-items: center; min-width: 0; }
.npp__input { box-sizing: border-box; width: 100%; min-height: var(--drama-control-height-sm); padding: 0 calc(var(--drama-space-sm) + 20px) 0 var(--drama-space-sm); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-bg); color: var(--drama-color-text); font: inherit; outline: none; }
.npp__input:focus { border-color: var(--drama-color-primary); }
.npp__input:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 1px; }
.npp__clear { position: absolute; right: var(--drama-space-xs); display: grid; place-items: center; padding: 2px; border: 0; border-radius: var(--drama-radius-sm); background: none; color: var(--drama-color-text-muted); cursor: pointer; }
.npp__clear:hover { color: var(--drama-color-text); }
.npp__clear:focus-visible { outline: 2px solid var(--drama-color-primary); }
.npp__panel { position: fixed; z-index: 1000; box-sizing: border-box; max-height: 280px; margin: 0; padding: var(--drama-space-xs); overflow-y: auto; list-style: none; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); box-shadow: var(--drama-shadow-md); }
.npp__option { display: grid; gap: 2px; padding: var(--drama-space-xs) var(--drama-space-sm); border-radius: var(--drama-radius-sm); cursor: pointer; }
.npp__option.is-active { background: var(--drama-color-surface-alt); }
.npp__title { overflow: hidden; color: var(--drama-color-text); text-overflow: ellipsis; white-space: nowrap; }
.npp__meta { overflow: hidden; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); text-overflow: ellipsis; white-space: nowrap; }
.npp__note { padding: var(--drama-space-xs) var(--drama-space-sm); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.npp__note--error { color: var(--drama-color-danger); }
</style>
